<template>
	<div
		class="dropdown-wrapper"
		ref="wrapperRef"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		<div
			class="dropdown-activator"
			@click.capture="handleActivatorClickCapture"
			@click.stop="handleActivatorClick"
			@mouseenter="handleActivatorMouseEnter"
			@mouseleave="handleActivatorMouseLeave"
		>
			<slot name="activator" />
		</div>

		<Teleport
			v-if="isOpen"
			to="body"
		>
			<div
				v-if="isCenteredOnMobile || isBottomSheetOnMobile"
				class="dropdown-mobile-backdrop"
				:style="backdropStyles"
				@mousedown.stop
				@click.stop="handleBackdropClick"
				@wheel.prevent
				@touchmove.prevent
			/>
			<div
				ref="contentRef"
				class="dropdown-content"
								:class="[
					`dropdown-position-${computedPosition}`,
					contentClass,
					{ 'dropdown-resizable': resizable },
					{ 'dropdown-fullscreen': isFullscreen },
					{ 'dropdown-centered-mobile': isCenteredOnMobile },
					{ 'dropdown-bottom-sheet-mobile': isBottomSheetOnMobile },
					{ 'dropdown-auto-size': isAutoSize },
					{ 'dropdown-content--hover': canUseHover },
					`dropdown-corners-${computedPosition}`
				]"
				:style="contentStyles"
				@mousedown.stop
				@mouseenter="handleContentMouseEnter"
				@mouseleave="handleContentMouseLeave"
			>
				<div class="dropdown-content-inner">
					<div
						v-if="isBottomSheetOnMobile"
						class="dropdown-bottom-sheet-header"
					>
						<span>{{ mobileTitle || $t('contact_choose_action') }}</span>
						<button
							type="button"
							class="dropdown-bottom-sheet-close"
							:aria-label="$t('close')"
							@click.stop="closeDropdown"
						>
							<app-icon name="close" width="18px" color="#979797" />
						</button>
					</div>
					<slot />
				</div>

				<template v-if="resizable && !isFullscreen && !isBottomSheetOnMobile">
					<div
						v-for="corner in resizeCorners"
						:key="corner.position"
						class="resize-handle-icon"
						:class="`resize-handle-${corner.position}`"
						:style="{ transform: `rotate(${getRotationAngle(corner.position)}deg)` }"
						@mousedown="(e) => startResize(e, corner.direction)"
					>
						<app-icon
							name="arrow-dropdown-resize"
							width="11px"
							color="#979797"
							hover-color="#499259"
							:title="$t('ui_resize')"
						/>
					</div>
				</template>
			</div>
		</Teleport>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	watch,
	onMounted,
	onUnmounted,
	nextTick,
	provide,
	CSSProperties
} from 'vue'
import { useRoute } from 'vue-router'
import AppIcon from "@/components/ui-kit/icon/AppIcon.vue";

type ResizeState = {
	startX: number
	startY: number
	lastX: number
	lastY: number
	direction: string
}

type Props = {
	position?: 'left' | 'right' | 'bottom'
	initialWidth?: number
	initialHeight?: number
	minWidth?: number
	minHeight?: number
	maxWidth?: number
	maxHeight?: number
	contentZIndex?: number
	resizable?: boolean
	closeByClickOutside?: boolean
	closeByEsc?: boolean
	hideByParentScroll?: boolean
	// CSS-селектор элементов вне дропдауна, клик по которым НЕ закрывает его
	ignoreClickOutside?: string
	name?: string
	trigger?: 'click' | 'hover'
	hoverDelay?: number
	hoverCloseDelay?: number
	fullscreenOnMobile?: boolean
	centeredOnMobile?: boolean
	bottomSheetOnMobile?: boolean
	mobileTitle?: string
	contentClass?: string
}

const props = withDefaults(defineProps<Props>(), {
	position: 'bottom',
	minWidth: 300,
	minHeight: 300,
	maxWidth: 600,
	maxHeight: 600,
	resizable: false,
	closeByClickOutside: true,
	closeByEsc: true,
	hideByParentScroll: true,
	trigger: 'click',
	hoverDelay: 600,
	hoverCloseDelay: 50,
	fullscreenOnMobile: false,
	centeredOnMobile: false,
	bottomSheetOnMobile: true,
	mobileTitle: '',
	contentClass: ''
})

const emit = defineEmits<{
	open: []
	close: []
}>()

const isOpen = ref(false)
const wrapperRef = ref<HTMLElement>()
const contentRef = ref<HTMLElement>()
const contentWidth = ref(props.initialWidth ?? 0)
const contentHeight = ref(props.initialHeight ?? 0)
const isResizing = ref(false)
const isFullscreen = ref(false)
const isCenteredOnMobile = ref(false)
const isBottomSheetOnMobile = ref(false)

const isAutoSize = computed(() => !props.resizable && props.initialWidth == null && props.initialHeight == null)

const backdropStyles = computed<CSSProperties>(() => ({
	zIndex: (props.contentZIndex ?? 10001) - 1
}))

const autoSizeRecalcTrigger = ref(0)

let openTimer: ReturnType<typeof setTimeout> | null = null
let closeTimer: ReturnType<typeof setTimeout> | null = null

// Флаг для отслеживания нахождения курсора над дропдауном
const isHoveringDropdown = ref(false)

// Отслеживание скролла родителя
let lastPosition = ref<{ top: number; left: number } | null>(null)

let scrollCleanups: (() => void)[] = []

let resizeState: ResizeState | null = null

const route = useRoute()

const canUseHover = computed(() =>
	props.trigger === 'hover' && !props.resizable && !isBottomSheetOnMobile.value
)

const computedPosition = computed(() => {
	if (isFullscreen.value || isBottomSheetOnMobile.value) return 'bottom'
	return calculateOptimalPosition()
})

const contentStyles = computed<CSSProperties>(() => {
	if (isFullscreen.value) {
		return {
			width: '100vw',
			height: '100dvh',
			zIndex: props.contentZIndex ?? 10001
		}
	}

	if (isBottomSheetOnMobile.value) {
		return {
			left: '0',
			right: '0',
			top: 'auto',
			bottom: '0',
			width: '100vw',
			height: 'auto',
			maxWidth: '100vw',
			maxHeight: 'calc(100dvh - 44px)',
			transform: 'none',
			zIndex: props.contentZIndex ?? 10001
		}
	}

	const styles: CSSProperties = {}

	if (props.contentZIndex != null) {
		styles.zIndex = props.contentZIndex
	}

	// Подписываемся на триггер для пересчёта позиции в авто-режиме
	// eslint-disable-next-line no-unused-expressions
	autoSizeRecalcTrigger.value

	if (isAutoSize.value) {
		styles.width = 'auto'
		styles.height = 'auto'
		styles.maxWidth = `${props.maxWidth}px`
		styles.maxHeight = `${props.maxHeight}px`
	} else {
		styles.width = `${contentWidth.value}px`
		styles.height = `${contentHeight.value}px`
	}

	if (isCenteredOnMobile.value) {
		styles.top = '50%'
		styles.left = '50%'
		styles.right = 'auto'
		styles.bottom = 'auto'
		styles.transform = 'translate(-50%, -50%)'
		styles.maxWidth = 'calc(100vw - 32px)'
		styles.maxHeight = 'calc(100dvh - 32px)'
		return styles
	}

	if (!isOpen.value || !wrapperRef.value) return styles

	const rect = wrapperRef.value.getBoundingClientRect()
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const gap = 5
	const minDistance = 20

	const elWidth = isAutoSize.value
		? (contentRef.value?.offsetWidth ?? 0)
		: contentWidth.value
	const elHeight = isAutoSize.value
		? (contentRef.value?.offsetHeight ?? 0)
		: contentHeight.value

	switch (computedPosition.value) {
		case 'bottom': {
			let top = rect.bottom + gap
			let left = rect.left + rect.width / 2 - elWidth / 2

			// Проверяем, помещается ли снизу
			if (top + elHeight + minDistance > viewportHeight) {
				// Помещаем сверху
				top = rect.top - gap - elHeight
			}

			// Корректируем по горизонтали
			if (left < minDistance) left = minDistance
			if (left + elWidth + minDistance > viewportWidth) {
				left = viewportWidth - elWidth - minDistance
			}

			styles.top = `${top}px`
			styles.left = `${left}px`
			break
		}

		case 'left': {
			let top = rect.top
			let left = rect.left - gap - elWidth

			// Корректируем по вертикали
			if (top < minDistance) top = minDistance
			if (top + elHeight + minDistance > viewportHeight) {
				top = viewportHeight - elHeight - minDistance
			}

			styles.top = `${top}px`
			styles.left = `${left}px`
			break
		}

		case 'right': {
			let top = rect.top
			let left = rect.right + gap

			// Корректируем по вертикали
			if (top < minDistance) top = minDistance
			if (top + elHeight + minDistance > viewportHeight) {
				top = viewportHeight - elHeight - minDistance
			}

			styles.top = `${top}px`
			styles.left = `${left}px`
			break
		}
	}

	return styles
})

const resizeCorners = computed(() => {
	const corners = []

	switch (computedPosition.value) {
		case 'left':
			corners.push(
				{ position: 'top-left', direction: 'top-left' },
				{ position: 'bottom-left', direction: 'bottom-left' }
			)
			break
		case 'right':
			corners.push(
				{ position: 'top-right', direction: 'top-right' },
				{ position: 'bottom-right', direction: 'bottom-right' }
			)
			break
		case 'bottom':
			corners.push(
				{ position: 'bottom-left', direction: 'bottom-left' },
				{ position: 'bottom-right', direction: 'bottom-right' }
			)
			break
	}

	return corners
})

// Метод для получения угла поворота иконки в зависимости от позиции
const getRotationAngle = (position: string): number => {
	switch (position) {
		case 'top-right':    // Правый верхний угол
			return 0
		case 'bottom-right': // Правый нижний угол
			return 90     // Поворот на 90°
		case 'bottom-left':  // Левый нижний угол
			return 180    // Поворот на 180°
		case 'top-left':     // Левый верхний угол
			return 270    // Поворот на 270°
		default:
			return 0
	}
}

const clearTimers = () => {
	if (openTimer) {
		clearTimeout(openTimer)
		openTimer = null
	}
	if (closeTimer) {
		clearTimeout(closeTimer)
		closeTimer = null
	}
}

const handleMouseEnter = () => {
	if (!canUseHover.value || isResizing.value) return
	clearTimers()

	openTimer = setTimeout(() => {
		if (!isOpen.value) {
			openDropdown()
		}
	}, props.hoverDelay)
}

const handleMouseLeave = () => {
	if (!canUseHover.value || isResizing.value) return

	// Если курсор не над контентом дропдауна, начинаем отсчет до закрытия
	if (!isHoveringDropdown.value) {
		scheduleClose()
	}
}

const handleActivatorMouseEnter = () => {
	if (!canUseHover.value) return
	handleMouseEnter()
}

const handleActivatorMouseLeave = () => {
	if (!canUseHover.value) return
	handleMouseLeave()
}

const handleContentMouseEnter = () => {
	if (!canUseHover.value) return
	isHoveringDropdown.value = true
	clearTimers()
}

const handleContentMouseLeave = () => {
	if (!canUseHover.value) return
	isHoveringDropdown.value = false
	scheduleClose()
}

const scheduleClose = () => {
	if (!canUseHover.value || isResizing.value) return

	clearTimers()
	closeTimer = setTimeout(() => {
		if (isOpen.value && !isHoveringDropdown.value) {
			closeDropdown()
		}
	}, props.hoverCloseDelay)
}


const handleActivatorClick = () => {
	if (canUseHover.value) {
		if (isOpen.value) {
			closeDropdown()
		} else {
			openDropdown()
		}
	} else {
		toggleDropdown()
	}
}

const handleActivatorClickCapture = (event: MouseEvent) => {
	if (!isBottomSheetOnMobile.value) return

	event.preventDefault()
	event.stopPropagation()
	handleActivatorClick()
}

const calculateOptimalPosition = () => {
	if (!wrapperRef.value || !isOpen.value) return props.position

	const rect = wrapperRef.value.getBoundingClientRect()
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20
	const gap = 5

	const elWidth = isAutoSize.value
		? (contentRef.value?.offsetWidth ?? 0)
		: contentWidth.value
	const elHeight = isAutoSize.value
		? (contentRef.value?.offsetHeight ?? 0)
		: contentHeight.value

	switch (props.position) {
		case 'left': {
			const leftSpace = rect.left - elWidth - gap
			if (leftSpace >= minDistance) return 'left'
			return 'right'
		}

		case 'right': {
			const rightSpace = viewportWidth - rect.right - elWidth - gap
			if (rightSpace >= minDistance) return 'right'
			return 'left'
		}

		case 'bottom': {
			const bottomSpace = viewportHeight - rect.bottom - elHeight - gap
			if (bottomSpace >= minDistance) return 'bottom'
			// Если снизу не хватает места, показываем сверху
			return 'bottom' // Будет откорректировано в contentStyles
		}

		default:
			return props.position
	}
}

// Получаем максимально доступные размеры для текущей позиции
const getMaxAvailableSize = () => {
	const rect = wrapperRef.value?.getBoundingClientRect()
	if (!rect) return { maxWidth: props.maxWidth, maxHeight: props.maxHeight }

	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20

	let maxWidth = props.maxWidth
	let maxHeight = props.maxHeight

	switch (computedPosition.value) {
		case 'left':
			// Максимальная ширина - до левого края экрана с отступом
			maxWidth = Math.min(maxWidth, rect.left - minDistance - 10)
			// Максимальная высота - высота viewport с отступами
			maxHeight = Math.min(maxHeight, viewportHeight - minDistance * 2)
			break

		case 'right':
			// Максимальная ширина - до правого края экрана с отступом
			maxWidth = Math.min(maxWidth, viewportWidth - rect.right - minDistance - 10)
			// Максимальная высота - высота viewport с отступами
			maxHeight = Math.min(maxHeight, viewportHeight - minDistance * 2)
			break

		case 'bottom': {
			// Максимальная высота - доступное пространство сверху или снизу
			const availableBottomSpace = viewportHeight - rect.bottom - minDistance - 10
			const availableTopSpace = rect.top - minDistance - 10
			maxHeight = Math.min(maxHeight, Math.max(availableBottomSpace, availableTopSpace))
			// Максимальная ширина - ширина viewport с отступами
			maxWidth = Math.min(maxWidth, viewportWidth - minDistance * 2)
			break
		}
	}

	return { maxWidth, maxHeight }
}

// Проверка размеров из LS на соответствие текущему viewport
const validateSavedSize = (savedWidth: number, savedHeight: number) => {
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20
	const maxAvailableWidth = viewportWidth - minDistance * 2
	const maxAvailableHeight = viewportHeight - minDistance * 2

	// Ограничиваем сохраненные размеры максимально доступными
	const validWidth = Math.min(savedWidth, maxAvailableWidth)
	const validHeight = Math.min(savedHeight, maxAvailableHeight)

	// Проверяем минимальные размеры
	return {
		width: Math.max(props.minWidth, validWidth),
		height: Math.max(props.minHeight, validHeight)
	}
}

const toggleDropdown = () => {
	if (isOpen.value) {
		closeDropdown()
	} else {
		openDropdown()
	}
}

const openDropdown = async () => {
	if (isOpen.value) return

	if (!isAutoSize.value) {
		if (props.name) {
			const savedSize = loadSizeFromStorage()
			if (savedSize) {
				const validatedSize = validateSavedSize(savedSize.width, savedSize.height)
				contentWidth.value = validatedSize.width
				contentHeight.value = validatedSize.height
			} else {
				contentWidth.value = props.initialWidth ?? props.minWidth
				contentHeight.value = props.initialHeight ?? props.minHeight
			}
		} else {
			contentWidth.value = props.initialWidth ?? props.minWidth
			contentHeight.value = props.initialHeight ?? props.minHeight
		}
	}

	isOpen.value = true
	isHoveringDropdown.value = false
	emit('open')

	await nextTick()
	updateFullscreenState()

	if (isAutoSize.value) {
		await nextTick()
		autoSizeRecalcTrigger.value++
	}

	if (!canUseHover.value) {
		document.addEventListener('mousedown', handleClickOutside)
	}
}

const closeDropdown = () => {
	isOpen.value = false
	isHoveringDropdown.value = false
	clearTimers()

	if (!canUseHover.value) {
		document.removeEventListener('mousedown', handleClickOutside)
	}
	emit('close')
}

provide('appResizableDropdownClose', closeDropdown)

const handleBackdropClick = () => {
	if (isBottomSheetOnMobile.value) {
		closeDropdown()
	}
}

const handleClickOutside = (event: MouseEvent) => {
	if (!props.closeByClickOutside || isResizing.value || canUseHover.value) return
	const target = event.target as HTMLElement | null
	if (props.ignoreClickOutside && target?.closest(props.ignoreClickOutside)) {
		return
	}
	if (!contentRef.value?.contains(event.target as Node) &&
		!wrapperRef.value?.contains(event.target as Node)) {
		closeDropdown()
	}
}

const handleEsc = (event: KeyboardEvent) => {
	if (event.key !== 'Escape') {
		return
	}
	if (event.defaultPrevented) {
		return
	}
	if (!isOpen.value) {
		return
	}
	closeDropdown()
}

const startResize = (e: MouseEvent, direction: string) => {
	e.preventDefault()
	e.stopPropagation()

	isResizing.value = true

	resizeState = {
		startX: e.clientX,
		startY: e.clientY,
		lastX: e.clientX,
		lastY: e.clientY,
		direction
	}

	document.addEventListener('mousemove', handleResize)
	document.addEventListener('mouseup', stopResize)
}

const handleResize = (e: MouseEvent) => {
	if (!resizeState || !contentRef.value || !wrapperRef.value) return

	const deltaX = e.clientX - resizeState.lastX
	const deltaY = e.clientY - resizeState.lastY

	// Обновляем последнюю позицию
	resizeState.lastX = e.clientX
	resizeState.lastY = e.clientY

	// Используем текущие размеры как основу
	let newWidth = contentWidth.value
	let newHeight = contentHeight.value

	// Получаем текущие координаты
	const rect = wrapperRef.value.getBoundingClientRect()
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minDistance = 20
	const gap = 5

	// Вычисляем текущие границы контентного блока
	let currentTop = 0
	let currentBottom = 0
	let currentLeft = 0
	let currentRight = 0

	switch (computedPosition.value) {
		case 'bottom': {
			const top = parseFloat(contentStyles.value.top?.toString() || '0')
			const left = parseFloat(contentStyles.value.left?.toString() || '0')
			currentTop = top
			currentBottom = top + newHeight
			currentLeft = left
			currentRight = left + newWidth
			break
		}
		case 'left': {
			currentTop = rect.top
			currentBottom = rect.top + newHeight
			currentRight = rect.left - gap
			currentLeft = currentRight - newWidth
			break
		}
		case 'right': {
			currentTop = rect.top
			currentBottom = rect.top + newHeight
			currentLeft = rect.right + gap
			currentRight = currentLeft + newWidth
			break
		}
	}

	switch (resizeState.direction) {
		case 'top-left':
		case 'top-right':
			if (computedPosition.value === 'left' || computedPosition.value === 'right') {
				if (deltaY < 0) {
					// Увеличение высоты - поднимаем верхнюю границу
					const wouldBeTop = currentTop - Math.abs(deltaY)
					if (wouldBeTop >= minDistance) {
						newHeight += Math.abs(deltaY)
					}
				} else if (deltaY > 0) {
					// Уменьшение высоты - опускаем верхнюю границу
					// Проверяем, что не достигли нижней границы
					const wouldBeBottom = currentBottom - Math.abs(deltaY)
					if (wouldBeBottom - minDistance >= currentTop) { // Минимальная высота
						newHeight -= Math.abs(deltaY)
					}
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaY < 0) {
					// Уменьшение высоты - проверяем верхнюю границу
					const wouldBeTop = currentTop - Math.abs(deltaY)
					if (wouldBeTop >= minDistance) {
						newHeight -= Math.abs(deltaY)
					}
				} else if (deltaY > 0) {
					// Увеличение высоты - проверяем нижнюю границу
					const wouldBeBottom = currentBottom + Math.abs(deltaY)
					if (wouldBeBottom + minDistance <= viewportHeight) {
						newHeight += Math.abs(deltaY)
					}
				}
			}
			break

		case 'bottom-left':
		case 'bottom-right':
			if (computedPosition.value === 'left' || computedPosition.value === 'right') {
				if (deltaY > 0) {
					// Увеличение высоты - опускаем нижнюю границу
					const wouldBeBottom = currentBottom + Math.abs(deltaY)
					if (wouldBeBottom + minDistance <= viewportHeight) {
						newHeight += Math.abs(deltaY)
					}
				} else if (deltaY < 0) {
					// Уменьшение высоты - поднимаем нижнюю границу
					// Проверяем, что не достигли верхней границы
					const wouldBeTop = currentTop + Math.abs(deltaY)
					if (wouldBeTop + minDistance <= currentBottom) { // Минимальная высота
						newHeight -= Math.abs(deltaY)
					}
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaY > 0) {
					// Увеличение высоты - проверяем нижнюю границу
					const wouldBeBottom = currentBottom + Math.abs(deltaY)
					if (wouldBeBottom + minDistance <= viewportHeight) {
						newHeight += Math.abs(deltaY)
					}
				} else if (deltaY < 0) {
					// Уменьшение высоты - проверяем верхнюю границу
					const wouldBeTop = currentTop - Math.abs(deltaY)
					if (wouldBeTop >= minDistance) {
						newHeight -= Math.abs(deltaY)
					}
				}
			}
			break
	}

	switch (resizeState.direction) {
		case 'top-left':
		case 'bottom-left':
			if (computedPosition.value === 'left') {
				if (deltaX < 0) {
					const wouldBeLeft = currentLeft + deltaX
					if (wouldBeLeft >= minDistance) {
						newWidth -= deltaX
					}
				} else if (deltaX > 0) {
					newWidth -= deltaX
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaX < 0) {
					const wouldBeLeft = currentLeft + deltaX
					if (wouldBeLeft >= minDistance) {
						newWidth += deltaX
					}
				} else if (deltaX > 0) {
					const wouldBeRight = currentRight + deltaX
					if (wouldBeRight + minDistance <= viewportWidth) {
						newWidth += deltaX
					}
				}
			}
			break

		case 'top-right':
		case 'bottom-right':
			if (computedPosition.value === 'right') {
				if (deltaX > 0) {
					const wouldBeRight = currentRight + deltaX
					if (wouldBeRight + minDistance <= viewportWidth) {
						newWidth += deltaX
					}
				} else if (deltaX < 0) {
					newWidth += deltaX
				}
			} else if (computedPosition.value === 'bottom') {
				if (deltaX < 0) {
					const wouldBeLeft = currentLeft + deltaX
					if (wouldBeLeft >= minDistance) {
						newWidth += deltaX
					}
				} else if (deltaX > 0) {
					const wouldBeRight = currentRight + deltaX
					if (wouldBeRight + minDistance <= viewportWidth) {
						newWidth += deltaX
					}
				}
			}
			break
	}

	// Получаем максимальные доступные размеры
	const { maxWidth, maxHeight } = getMaxAvailableSize()
	const effectiveMaxWidth = Math.min(maxWidth, props.maxWidth)
	const effectiveMaxHeight = Math.min(maxHeight, props.maxHeight)

	// Применяем ограничения min/max
	newWidth = Math.max(props.minWidth, Math.min(newWidth, effectiveMaxWidth))
	newHeight = Math.max(props.minHeight, Math.min(newHeight, effectiveMaxHeight))

	contentWidth.value = newWidth
	contentHeight.value = newHeight
}

const stopResize = () => {
	isResizing.value = false
	clearTimers()

	if (props.name) {
		saveSizeToStorage(contentWidth.value, contentHeight.value)
	}

	resizeState = null
	document.removeEventListener('mousemove', handleResize)
	document.removeEventListener('mouseup', stopResize)
}

const loadSizeFromStorage = () => {
	if (!props.name) return null
	const data = localStorage.getItem(`dropdown-${props.name}`)
	if (!data) return null

	try {
		const { width, height } = JSON.parse(data)
		return {
			width: Math.max(props.minWidth, Math.min(width, props.maxWidth)),
			height: Math.max(props.minHeight, Math.min(height, props.maxHeight))
		}
	} catch {
		return null
	}
}

const saveSizeToStorage = (width: number, height: number) => {
	if (!props.name) return

	// Валидируем размеры перед сохранением
	const validatedSize = validateSavedSize(width, height)

	localStorage.setItem(
		`dropdown-${props.name}`,
		JSON.stringify({
			width: validatedSize.width,
			height: validatedSize.height
		})
	)
}

const checkParentScroll = () => {
	if (!wrapperRef.value || !isOpen.value || !props.hideByParentScroll) return

	const rect = wrapperRef.value.getBoundingClientRect()

	if (lastPosition.value) {
		// Уменьшаем допуск до 5px
		const positionChanged =
			Math.abs(rect.top - lastPosition.value.top) > 5 ||
			Math.abs(rect.left - lastPosition.value.left) > 5

		if (positionChanged) {
			closeDropdown()
		}
	}

	lastPosition.value = { top: rect.top, left: rect.left }
}

const updateFullscreenState = () => {
	const isMobileViewport = window.innerWidth <= 480
	isCenteredOnMobile.value = props.centeredOnMobile && isMobileViewport
	isFullscreen.value = props.fullscreenOnMobile && isMobileViewport && !isCenteredOnMobile.value
	isBottomSheetOnMobile.value = props.bottomSheetOnMobile && isMobileViewport &&
		!isCenteredOnMobile.value && !isFullscreen.value
}

onMounted(() => {
	updateFullscreenState()
	window.addEventListener('resize', updateFullscreenState)

	if (props.closeByEsc) {
		document.addEventListener('keydown', handleEsc)
	}

	if (props.hideByParentScroll) {
		const scrollObserver = () => {
			requestAnimationFrame(checkParentScroll)
		}

		const scrollableParents = findScrollableParents(wrapperRef.value)
		scrollableParents.forEach(parent => {
			parent.addEventListener('scroll', scrollObserver, { passive: true })
		})

		window.addEventListener('scroll', scrollObserver, { passive: true })

		scrollCleanups = [
			...scrollableParents.map(parent => () => parent.removeEventListener('scroll', scrollObserver)),
			() => window.removeEventListener('scroll', scrollObserver)
		]
	}
})

// Поиск всех скролящихся родителей
const findScrollableParents = (element: HTMLElement | null): HTMLElement[] => {
	const scrollableParents: HTMLElement[] = []
	let current = element?.parentElement

	while (current) {
		const style = window.getComputedStyle(current)
		const isScrollable = style.overflow === 'auto' ||
			style.overflow === 'scroll' ||
			style.overflowY === 'auto' ||
			style.overflowY === 'scroll'

		if (isScrollable) {
			scrollableParents.push(current)
		}

		current = current.parentElement
	}

	return scrollableParents
}

onUnmounted(() => {
	window.removeEventListener('resize', updateFullscreenState)
	document.removeEventListener('keydown', handleEsc)
	document.removeEventListener('mousedown', handleClickOutside)
	document.removeEventListener('mousemove', handleResize)
	document.removeEventListener('mouseup', stopResize)
	scrollCleanups.forEach(cleanup => cleanup())
	scrollCleanups = []
	clearTimers()
})

watch(isOpen, (newVal) => {
	if (newVal) {
		lastPosition.value = wrapperRef.value
			? wrapperRef.value.getBoundingClientRect()
			: null
	}
})

watch(() => route.fullPath, () => {
	if (isOpen.value) {
		closeDropdown()
	}
})

defineExpose({
	open: openDropdown,
	close: closeDropdown
})
</script>

<style scoped lang="scss">
.dropdown-wrapper {
	display: inline-block;
	position: relative;
}

.dropdown-mobile-backdrop {
	position: fixed;
	inset: 0;
	background: rgba(0, 0, 0, 0.4);
	touch-action: none;
	overscroll-behavior: contain;
}

.dropdown-activator {
	cursor: pointer;

	:deep(*) {
		cursor: pointer;
	}
}

.dropdown-content {
	position: fixed;
	z-index: 10001;
	background: white;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	box-shadow: 0 1px 4px 0 #00000033;
	box-sizing: border-box;
	overflow: hidden;

	&.dropdown-fullscreen {
		top: 0 !important;
		left: 0 !important;
		width: 100vw !important;
		height: 100vh !important;
		height: 100dvh !important;
		border-radius: 0;
		border: none;
		box-shadow: none;
	}

	&.dropdown-bottom-sheet-mobile {
		border: none;
		border-radius: 12px 12px 0 0;
		box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.12);

		.dropdown-content-inner {
			height: auto;
			max-height: calc(100dvh - 44px);
			overflow-y: auto;
		}
	}

	.dropdown-content-inner {
		width: 100%;
		height: 100%;
		overflow: auto;
		display: flex;
		flex-direction: column;
	}

	&.dropdown-resizable {
		resize: none;
	}

	&.dropdown-auto-size {
		display: flex;
		flex-direction: column;

		.dropdown-content-inner {
			width: auto;
			flex: 1;
			min-height: 0;
			overflow: hidden;
		}
	}

	&:hover {
		.resize-handle-icon {
			opacity: 1 !important;
		}
	}
}

@media (max-width: 480px) {
	.dropdown-content.dropdown-bottom-sheet-mobile {
		:deep(.app-tag-selector__close),
		:deep(.app-list-select-dropdown__close),
		:deep(.client-role-modal__close),
		:deep(.client-filter-modal__close),
		:deep(.add-client-modal__close),
		:deep(.add-counterparty-modal__close),
		:deep(.add-role-modal__close),
		:deep(.add-worker-modal__close),
		:deep(.reattach-modal__close) {
			display: none;
		}
	}
}

.dropdown-bottom-sheet-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: 42px;
	padding: 0 12px;
	border-bottom: 1px solid #E0E0E0;
	box-sizing: border-box;
	font-size: 12px;
	font-weight: 500;
	line-height: 16px;
	color: #4D4D4D;
}

.dropdown-bottom-sheet-close {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 28px;
	height: 28px;
	padding: 0;
	border: 0;
	background: transparent;
	cursor: pointer;
}

.resize-handle {
	position: absolute;
	width: 12px;
	height: 12px;
	background: #2196f3;
	border: 2px solid white;
	border-radius: 2px;
	cursor: nwse-resize;
	z-index: 10000;

	&:hover {
		background: #1976d2;
	}

	&-top-left {
		top: -3px;
		left: -3px;
		cursor: nw-resize;
	}

	&-top-right {
		top: -3px;
		right: -3px;
		cursor: ne-resize;
	}

	&-bottom-left {
		bottom: -3px;
		left: -3px;
		cursor: sw-resize;
	}

	&-bottom-right {
		bottom: -3px;
		right: -3px;
		cursor: se-resize;
	}
}

.resize-handle-icon {
	position: absolute;
	width: 20px;
	height: 20px;
	z-index: 10000;
	display: flex;
	align-items: center;
	justify-content: center;
	opacity: 0;
}

.dropdown-corners {
	&-left {
		.resize-handle-top-left,
		.resize-handle-bottom-left {
			display: flex;
		}
		.resize-handle-top-right,
		.resize-handle-bottom-right {
			display: none;
		}
	}

	&-right {
		.resize-handle-top-right,
		.resize-handle-bottom-right {
			display: flex;
		}
		.resize-handle-top-left,
		.resize-handle-bottom-left {
			display: none;
		}
	}

	&-bottom {
		.resize-handle-bottom-left,
		.resize-handle-bottom-right {
			display: flex;
		}
		.resize-handle-top-left,
		.resize-handle-top-right {
			display: none;
		}
	}
}

// @media (max-width: 480px) {
// 	.dropdown-content:not(.dropdown-fullscreen) {
// 		&.dropdown-position-left,
// 		&.dropdown-position-right,
// 		&.dropdown-position-bottom {
// 			top: 0 !important;
// 			left: 0 !important;
// 			width: 100vw !important;
// 			height: 100vh !important;
// 			border-radius: 0;
// 			border: none;
// 			box-shadow: none;
// 			overflow: visible !important;

// 			.resize-handle {
// 				display: none !important;
// 			}
// 		}
// 	}
// }
</style>
